import { useMemo, useState, useCallback } from 'react';
import type { Instance, Pims } from '@v2/types';
import type { SelectOption } from '@v2/components';
import type { InstanceFilter, FilterData } from '../components';

interface UseAdvancedFiltersResult {
  filters: InstanceFilter[];
  pimsFilter: string;
  setPimsFilter: (value: string) => void;
  pimsOptions: SelectOption[];
  isAddFilterModalOpen: boolean;
  filterToEdit: (FilterData & { index: number }) | null;
  filteredInstances: Instance[];
  openAddFilterModal: () => void;
  openEditFilterModal: (filter: InstanceFilter, index: number) => void;
  closeFilterModal: () => void;
  handleSubmitFilter: (filter: FilterData) => void;
  handleRemoveFilter: (index: number) => void;
}

export const useAdvancedFilters = (
  instances: Instance[],
  pims: Pims[],
): UseAdvancedFiltersResult => {
  const [filters, setFilters] = useState<InstanceFilter[]>([]);
  const [pimsFilter, setPimsFilter] = useState('');
  const [isAddFilterModalOpen, setIsAddFilterModalOpen] = useState(false);
  const [filterToEdit, setFilterToEdit] = useState<(FilterData & { index: number }) | null>(null);

  const pimsOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'All PIMS' },
      ...pims
        .filter(p => p.supportsConnectivity && p.name !== 'Other')
        .map(p => ({ value: p.id, label: p.name })),
    ],
    [pims],
  );

  const filteredInstances = useMemo(() => {
    let result = instances;

    if (pimsFilter) {
      result = result.filter(inst => inst.practice?.pimsInfo?.id === pimsFilter);
    }

    filters.forEach(filter => {
      if (filter.filterType === 'instance' && filter.filterData.length > 0) {
        result = result.filter(inst => filter.filterData.includes(inst.id.toLowerCase()));
      } else if (filter.filterType === 'pims' && filter.filterData.length > 0) {
        result = result.filter(inst =>
          filter.filterData.includes(inst.practice?.pimsInfo?.displayName ?? ''),
        );
      }
    });

    return result;
  }, [instances, filters, pimsFilter]);

  const openAddFilterModal = useCallback(() => {
    setFilterToEdit(null);
    setIsAddFilterModalOpen(true);
  }, []);

  const openEditFilterModal = useCallback((filter: InstanceFilter, index: number) => {
    setFilterToEdit({
      filterType: filter.filterType,
      filterData: filter.filterData,
      index,
    });
    setIsAddFilterModalOpen(true);
  }, []);

  const closeFilterModal = useCallback(() => {
    setIsAddFilterModalOpen(false);
    setFilterToEdit(null);
  }, []);

  const handleSubmitFilter = useCallback(
    (filter: FilterData) => {
      const entry: InstanceFilter = {
        filterType: filter.filterType,
        filterData: filter.filterData,
      };

      if (filterToEdit !== null && filterToEdit.index >= 0) {
        setFilters(prev => prev.map((el, idx) => (idx === filterToEdit.index ? entry : el)));
        setFilterToEdit(null);
      } else {
        setFilters(prev => [...prev, entry]);
      }
    },
    [filterToEdit],
  );

  const handleRemoveFilter = useCallback((index: number) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  }, []);

  return {
    filters,
    pimsFilter,
    setPimsFilter,
    pimsOptions,
    isAddFilterModalOpen,
    filterToEdit,
    filteredInstances,
    openAddFilterModal,
    openEditFilterModal,
    closeFilterModal,
    handleSubmitFilter,
    handleRemoveFilter,
  };
};
